import * as THREE from 'three';

import { pointAtStation, type Alignment } from '@/core/alignment';
import { sampleStations } from '@/lib/stationVolumes/swathGeometry';
import type { ClearanceVertex } from './types';

/**
 * Hard ceiling on ring count, whatever the corridor length (ruling P8): budget
 * is `MAX_ENVELOPE_RINGS` rings x <=64 profile vertices <= 76 800 vertices,
 * one draw call. A 20 km alignment at a 1 m floor would otherwise be 20 000
 * rings.
 */
export const MAX_ENVELOPE_RINGS = 1200;

/**
 * The uniform sample step that keeps `sampleStations`' UNIFORM component near
 * `MAX_ENVELOPE_RINGS` for a span of any length — there is deliberately no
 * upper clamp on the step (the previous `min(5, …)` dialog-preview formula):
 * capping the step at 5 m lets a 20 km run sample ~4 000 rings, four times
 * the budget. This alone does not GUARANTEE the cap — `sampleStations` also
 * inserts a ring at every alignment vertex inside the range, which a long,
 * dense polyline can push past the uniform count — so `capRingStations`
 * below is the actual enforcement point; this is just what keeps it from
 * having to do much work in the common case.
 */
export function envelopeSampleStep(span: number): number {
  return Math.max(1, span / MAX_ENVELOPE_RINGS);
}

/**
 * Decimate a station list to at most `maxRings + 1` entries, keeping the
 * first and last station exactly (so the corridor's requested range is never
 * shortened) and picking evenly-spaced indices in between. This is the HARD
 * cap (P8): whatever `sampleStations` produced — uniform samples plus one
 * ring per alignment vertex inside the range — never reaches the renderer
 * uncapped.
 */
function capRingStations(stations: Float64Array, maxRings: number): Float64Array {
  if (stations.length <= maxRings + 1) return stations;
  const out = new Float64Array(maxRings + 1);
  const last = stations.length - 1;
  for (let i = 0; i <= maxRings; i++) {
    out[i] = stations[Math.round((i * last) / maxRings)];
  }
  return out;
}

/**
 * The swept clearance envelope as a closed tube: the profile polygon placed in
 * the alignment's normal/up frame at every sampled station, stitched into a
 * quad strip per profile edge, with two end caps.
 *
 * Station sampling is shared with the station-volumes swath
 * (`sampleStations`), so a corner vertex is never cut: a uniform sampler walks
 * past a 90° turn and the ribbon mitres visibly wrong exactly where a reader
 * is looking hardest. The result is then hard-capped at `MAX_ENVELOPE_RINGS`
 * (P8) — see `capRingStations`.
 *
 * Vertices are relative to `origin` — UTM coordinates jitter visibly at
 * Float32 precision, so every 3D consumer of an alignment hangs off a
 * floating-origin group (CLAUDE.md's screen-space / float-origin rule).
 *
 * Pure and DOM-free so it is unit-testable without r3f.
 */
const ringPositions = (
  a: Alignment,
  profile: readonly ClearanceVertex[],
  verticalOffsetM: number,
  stations: Float64Array,
  origin: THREE.Vector3,
): Float32Array => {
  const m = profile.length;
  const out = new Float32Array(stations.length * m * 3);
  for (let i = 0; i < stations.length; i++) {
    const p = pointAtStation(a, stations[i]);
    // 2D left normal of the heading (tx, ty) is (-ty, tx). LEFT is a NEGATIVE
    // offset (core/alignment.ts's projectPoint convention), so a vertex at
    // offset o sits at -o along the left normal.
    const nx = -p.ty;
    const ny = p.tx;
    for (let k = 0; k < m; k++) {
      const [o, h] = profile[k];
      const w = (i * m + k) * 3;
      out[w] = p.x - nx * o - origin.x;
      out[w + 1] = p.y - ny * o - origin.y;
      out[w + 2] = p.z + verticalOffsetM + h - origin.z;
    }
  }
  return out;
};

/**
 * Whether every triangle of the tube has to be emitted reversed to face
 * outward. Shared by both builders — a banded tube that disagreed with the
 * plain one about winding would backface-cull only on the run source, which is
 * exactly the case nobody previews.
 *
 * Every face must wind counter-clockwise as seen from OUTSIDE the tube, or a
 * single-sided material backface-culls the walls and `computeVertexNormals()`
 * averages a wall's normal against an opposing cap's at the two end rings (the
 * fan reuses those rings' own vertices), producing meaningless shading there.
 *
 * Two things flip the handedness and both are handled here:
 *   1. The world basis is LEFT-handed. Forward is +tangent, up is +z, and a
 *      POSITIVE profile offset points RIGHT — i.e. along `-(−ty, tx)`. So a
 *      ring that is counter-clockwise in profile space comes out clockwise in
 *      world space, and the wall quads have to be emitted in the transposed
 *      order (`a0, a1, b0` / `b0, a1, b1`) to face outward.
 *   2. The profile's own winding is not fixed. `validateProfileVertices`
 *      deliberately imposes none — the worker's `signed_distance_to_polygon`
 *      uses an even-odd crossing test, which is winding-agnostic — so a
 *      clockwise polygon out of the sketch editor is perfectly legal and
 *      inverts everything again. Rather than adding a winding rule to all
 *      three validators, take the profile's shoelace sign here and reverse
 *      every triangle when it is negative.
 */
function profileFlip(profile: readonly ClearanceVertex[]): boolean {
  let shoelace = 0;
  const m = profile.length;
  for (let k = 0; k < m; k++) {
    const [x0, y0] = profile[k];
    const [x1, y1] = profile[(k + 1) % m];
    shoelace += x0 * y1 - y0 * x1;
  }
  return shoelace < 0;
}

export function buildEnvelopeGeometry(
  a: Alignment,
  profile: readonly ClearanceVertex[],
  verticalOffsetM: number,
  fromStation: number,
  toStation: number,
  sampleStep: number,
  origin: THREE.Vector3,
): THREE.BufferGeometry {
  const stations = capRingStations(sampleStations(a, fromStation, toStation, sampleStep), MAX_ENVELOPE_RINGS);
  const m = profile.length;
  const positions = ringPositions(a, profile, verticalOffsetM, stations, origin);

  const index: number[] = [];
  const flip = profileFlip(profile);
  const tri = (i0: number, i1: number, i2: number) => {
    if (flip) index.push(i0, i2, i1);
    else index.push(i0, i1, i2);
  };

  for (let i = 0; i < stations.length - 1; i++) {
    for (let k = 0; k < m; k++) {
      const k2 = (k + 1) % m;
      const a0 = i * m + k;
      const b0 = i * m + k2;
      const a1 = (i + 1) * m + k;
      const b1 = (i + 1) * m + k2;
      tri(a0, a1, b0);
      tri(b0, a1, b1);
    }
  }
  // End caps as triangle fans off vertex 0 of the first and last rings. A fan
  // is only a correct tessellation for a CONVEX polygon, and a real gauge is
  // often concave — so on those the cap self-overlaps. That is accepted, not
  // overlooked: the caps exist to stop the tube reading as an open pipe in a
  // 20 %-opacity preview, they are never picked against or measured, and the
  // overlap is invisible at that opacity. Anything load-bearing would need a
  // real ear-clipping triangulation.
  const last = (stations.length - 1) * m;
  for (let k = 1; k < m - 1; k++) {
    tri(0, k, k + 1);
    tri(last, last + k + 1, last + k);
  }

  const geom = new THREE.BufferGeometry();
  geom.setAttribute('position', new THREE.BufferAttribute(positions, 3));
  geom.setIndex(index);
  geom.computeVertexNormals();
  return geom;
}

/**
 * The envelope's EDGES, split into the two families that want DIFFERENT
 * weights: `sections` (the end rings and the periodic ribs) and `rails` (the
 * longitudinal lines joining one station's ring to the next).
 *
 * They are separate geometries rather than one, because they say different
 * things. A section is a real measured cross-section — the shape the profile
 * actually has at that station — and should read at full strength. A rail is
 * only interpolation between two of them, and there is one per profile vertex
 * for every ring, so at full strength they mass into a solid tube that hides
 * both the sections and the points inside. Drawn lighter, they give the
 * envelope its length and depth without competing with the cross-sections or
 * the cloud they are there to help you judge.
 *
 * It used to draw the two end rings and nothing else. On a real run that is
 * effectively invisible — an 885 m corridor puts both rings hundreds of metres
 * away, so the envelope reads as a flat translucent ribbon lying on the
 * ground with no discernible height, and a point sitting well outside it looks
 * like it is inside. The rails are what make the tube read as a tube: they
 * trace the profile's own corners along the whole sweep, so the top, the
 * shoulders and the base are visible wherever the camera happens to be.
 *
 * Still `LineSegments` off explicit vertex pairs rather than
 * `wireframeGeometry`, for the reason the end-ring-only version gave: a
 * wireframe would cross-hatch every one of a thousand quads.
 *
 * Budget: rails cost `m * (rings - 1)` segments and rings are hard-capped at
 * `MAX_ENVELOPE_RINGS`, so an 11-vertex gauge at the cap costs ~13 200
 * segments — a few hundred KB, drawn in one call.
 */

/**
 * Roughly how many cross-section ribs to draw over the whole corridor.
 *
 * A fixed spacing in METRES cannot work at both ends of the range this tool
 * covers (a 40 m bridge check and a 20 km route), and ribs every ring would
 * be a solid wall of lines. A target count keeps the read the same at any
 * length: enough ribs to see the corridor turn and rise, few enough to see
 * through it.
 */
const TARGET_RIBS = 24;

export interface EnvelopeOutline {
  /** End rings + periodic ribs: real cross-sections, drawn at full strength. */
  sections: THREE.BufferGeometry;
  /** Longitudinal vertex sweeps: interpolation, drawn faint. */
  rails: THREE.BufferGeometry;
}

export function buildEnvelopeOutline(
  a: Alignment,
  profile: readonly ClearanceVertex[],
  verticalOffsetM: number,
  fromStation: number,
  toStation: number,
  sampleStep: number,
  origin: THREE.Vector3,
): EnvelopeOutline {
  const m = profile.length;
  const stations = capRingStations(
    sampleStations(a, fromStation, Math.max(fromStation, toStation), sampleStep),
    MAX_ENVELOPE_RINGS,
  );
  const rings = stations.length;
  const pos = ringPositions(a, profile, verticalOffsetM, stations, origin);

  // Which rings get a full closed loop: both ends always, then an evenly
  // spaced interior selection. `Set` because at a short span the stride can
  // land on an end ring, and drawing it twice would double its brightness
  // against the single-width rails.
  const loops = new Set<number>([0, rings - 1]);
  if (rings > 2) {
    const stride = Math.max(1, Math.round((rings - 1) / TARGET_RIBS));
    for (let i = stride; i < rings - 1; i += stride) loops.add(i);
  }

  const sectionVerts: number[] = [];
  const railVerts: number[] = [];
  const push = (into: number[], v: number) => {
    into.push(pos[v * 3], pos[v * 3 + 1], pos[v * 3 + 2]);
  };

  // Cross-sections: closed loops around the profile.
  for (const i of loops) {
    for (let k = 0; k < m; k++) {
      push(sectionVerts, i * m + k);
      push(sectionVerts, i * m + ((k + 1) % m));
    }
  }
  // Rails: each profile vertex swept from one ring to the next.
  for (let i = 0; i < rings - 1; i++) {
    for (let k = 0; k < m; k++) {
      push(railVerts, i * m + k);
      push(railVerts, (i + 1) * m + k);
    }
  }

  const geometryOf = (verts: number[]) => {
    const g = new THREE.BufferGeometry();
    g.setAttribute('position', new THREE.BufferAttribute(new Float32Array(verts), 3));
    return g;
  };
  return { sections: geometryOf(sectionVerts), rails: geometryOf(railVerts) };
}

/**
 * One station bin's span of the tube, and the colour its verdict resolves to.
 *
 * `color` is a plain CSS hex the CALLER has already resolved from the palette
 * (`clearanceBandColor` in `lib/clearance/bands.ts`, off the shared
 * `clearanceBandState` predicate — ruling P14). This module has no idea what a
 * violation is, which is what keeps the K2 ladder to one definition.
 */
export interface EnvelopeBand {
  fromStation: number;
  toStation: number;
  color: string;
}

export interface BandedEnvelope {
  /** One indexed mesh for the whole tube. Attributes: `position` (relative to
   *  the floating origin) and `color` (RGBA, itemSize 4 — three.js enables
   *  `USE_COLOR_ALPHA` on a 4-wide colour attribute, which is how the
   *  selection dim rides the vertex colours instead of a second material). */
  geometry: THREE.BufferGeometry;
  /** Vertex index -> index into the `bands` array. Picking reads
   *  `vertexBin[face.a]`, the same shape and the same call site the corridor
   *  plates used, so the envelope's picker cannot drift from the one the
   *  chart and the table were already wired to. */
  vertexBin: Int32Array;
}

/**
 * Alpha multiplier for a bin OUTSIDE the brush selection — the envelope's twin
 * of `BAND_DIM_ALPHA`. Higher than the plates' 0.35 because it multiplies a
 * lower base opacity: the tube has to still be VISIBLE outside the selection
 * (it is the only thing saying where the corridor goes), just quiet.
 */
export const ENVELOPE_DIM_ALPHA = 0.45;

/** How far a hovered bin is pulled toward the theme's contrast ink. Enough to
 *  find under the pointer, not so far that the verdict hue is lost. */
const ENVELOPE_HOVER_MIX = 0.4;

export interface EnvelopeEmphasis {
  /** Brush range as BIN INDICES, or null for "nothing selected, all at full". */
  selection: { fromBin: number; toBin: number } | null;
  hoverBin: number | null;
  /** Colour a hovered band is mixed toward — the theme's contrast ink, never a
   *  hard-coded white, which is invisible on a light-mode sky. */
  hoverInk: string;
}

const NO_EMPHASIS: EnvelopeEmphasis = { selection: null, hoverBin: null, hoverInk: '#ffffff' };

/**
 * Rewrite the RGBA `color` attribute for a new verdict palette, brush
 * selection and hover bin.
 *
 * Everything is resolved to a concrete RGBA on the CPU — no custom shader, no
 * per-band material array. One Float32 upload over geometry that never moves,
 * so a theme flip, a brush drag and a pointer move each cost one buffer write
 * rather than a re-sweep of the alignment.
 */
export function paintBandedEnvelope(
  built: BandedEnvelope,
  bands: readonly EnvelopeBand[],
  emphasis: EnvelopeEmphasis = NO_EMPHASIS,
): void {
  const attr = built.geometry.getAttribute('color') as THREE.BufferAttribute;
  const arr = attr.array as Float32Array;
  const { selection, hoverBin, hoverInk } = emphasis;
  const lo = selection ? Math.min(selection.fromBin, selection.toBin) : 0;
  const hi = selection ? Math.max(selection.fromBin, selection.toBin) : 0;
  const ink = new THREE.Color(hoverInk);
  // Parsed once per band, not once per vertex: a 250-bin run at 1 200 rings is
  // ~80 000 vertices, and `new THREE.Color(hex)` per vertex would re-parse the
  // same four strings tens of thousands of times on every pointer move.
  const resolved = bands.map((b, i) => {
    const c = new THREE.Color(b.color);
    if (hoverBin === i) c.lerp(ink, ENVELOPE_HOVER_MIX);
    const alpha = !selection || (i >= lo && i <= hi) ? 1 : ENVELOPE_DIM_ALPHA;
    return { c, alpha };
  });
  for (let v = 0; v < built.vertexBin.length; v++) {
    const hit = resolved[built.vertexBin[v]];
    if (!hit) continue;
    arr[v * 4] = hit.c.r;
    arr[v * 4 + 1] = hit.c.g;
    arr[v * 4 + 2] = hit.c.b;
    arr[v * 4 + 3] = hit.alpha;
  }
  attr.needsUpdate = true;
}

/**
 * The swept envelope as one tube per station BIN, each carrying its own
 * verdict colour — the object that replaced the flat corridor plates.
 *
 * The plates were drawn at the alignment datum, which for a profile whose base
 * sits at height 0 is the SAME PLANE as this tube's floor: two objects in one
 * plane saying different things, and the flat one read as the envelope's
 * bottom. There is one object now, and it carries the verdict, the selection
 * and the hover.
 *
 * **Each band owns its own rings**, including a DUPLICATED ring at the station
 * it shares with its neighbour. That duplication is the whole point: a shared
 * ring would interpolate a violation red into the clear grey next door across
 * a whole bin's length, so the boundary a reader is trying to locate would be
 * the one place the colour is a blend of two verdicts. Duplicated at the same
 * station, the colour steps at the bin edge and there is still no gap.
 *
 * Ring budget (ruling P8) is shared across the whole tube rather than granted
 * per band, but a band never drops below its two end rings — a band with one
 * ring is not a band. So a run with more bins than half the budget spends two
 * rings each and exceeds it; that floor is deliberate and is the only case
 * that does.
 *
 * Pure and DOM-free so the vertex/bin map, the winding and the boundary
 * duplication are unit-testable without r3f.
 */
export function buildBandedEnvelope(
  a: Alignment,
  profile: readonly ClearanceVertex[],
  verticalOffsetM: number,
  bands: readonly EnvelopeBand[],
  sampleStep: number,
  origin: THREE.Vector3,
): BandedEnvelope {
  const m = profile.length;
  // `capRingStations(s, n)` returns at most `n + 1` rings, so asking for
  // `perBand` GAPS is what keeps the sum at or under the global budget; the
  // `max(1, …)` is the two-end-rings floor documented above.
  const perBand = Math.max(1, Math.floor(MAX_ENVELOPE_RINGS / Math.max(1, bands.length)) - 1);
  const stationsPerBand = bands.map((b) =>
    capRingStations(sampleStations(a, b.fromStation, Math.max(b.fromStation, b.toStation), sampleStep), perBand),
  );

  let vertexCount = 0;
  for (const s of stationsPerBand) vertexCount += s.length * m;

  const positions = new Float32Array(vertexCount * 3);
  const colors = new Float32Array(vertexCount * 4);
  const vertexBin = new Int32Array(vertexCount);
  const index: number[] = [];
  const flip = profileFlip(profile);
  const tri = (i0: number, i1: number, i2: number) => {
    if (flip) index.push(i0, i2, i1);
    else index.push(i0, i1, i2);
  };

  let base = 0;
  for (let bi = 0; bi < bands.length; bi++) {
    const stations = stationsPerBand[bi];
    positions.set(ringPositions(a, profile, verticalOffsetM, stations, origin), base * 3);
    vertexBin.fill(bi, base, base + stations.length * m);

    for (let i = 0; i < stations.length - 1; i++) {
      for (let k = 0; k < m; k++) {
        const k2 = (k + 1) % m;
        const a0 = base + i * m + k;
        const b0 = base + i * m + k2;
        const a1 = base + (i + 1) * m + k;
        const b1 = base + (i + 1) * m + k2;
        tri(a0, a1, b0);
        tri(b0, a1, b1);
      }
    }
    // Caps only at the two ends of the WHOLE tube, never between bands: an
    // interior cap is a solid disc across the tube at every bin boundary, and
    // on a double-sided translucent material a reader looking down the
    // corridor would see 250 of them stacked into an opaque wall.
    if (bi === 0) {
      for (let k = 1; k < m - 1; k++) tri(base, base + k, base + k + 1);
    }
    if (bi === bands.length - 1) {
      const last = base + (stations.length - 1) * m;
      for (let k = 1; k < m - 1; k++) tri(last, last + k + 1, last + k);
    }
    base += stations.length * m;
  }

  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
  geometry.setAttribute('color', new THREE.BufferAttribute(colors, 4));
  geometry.setIndex(index);
  // No `computeVertexNormals()`: this tube is drawn with `meshBasicMaterial`,
  // which is unlit, so normals would be ~1 MB of buffer nothing ever samples.
  geometry.computeBoundingSphere();

  const built = { geometry, vertexBin };
  // Paint the base verdict colours here rather than leaving the attribute at
  // zero: an all-black tube for the one frame before the caller's effect runs
  // is a visible flash on every rebuild.
  paintBandedEnvelope(built, bands);
  return built;
}
